import { Component, Input, OnInit, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@env/environment';

import { DokumenteService, FileUpload, FileUploadResponse } from '../services/dokumente.service';
import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-dokumente',
  templateUrl: './dokumente.component.html',
  styleUrls: ['./dokumente.component.scss']
})
export class DokumenteComponent implements OnInit, OnChanges {
  @Input() tableName: string = 'standort'; // Default to standort
  @Input() recordId: string;
  @Input() clientId?: string; // when provided, enables client aggregation mode
  @Input() isClientView: boolean = false; // when true, aggregate all standort files for client
  @Input() isEmbedded: boolean = false;
  @Input() parentContext?: string;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'filename',
    'type_size',
    'uploaded_by',
    'upload_date',
    'actions'
  ];

  dataSource: MatTableDataSource<FileUpload>;
  isLoadingResults = true;
  isRateLimitReached = false;
  totalItems = 0;
  pageSizes = [10, 25, 50, 100];
  searchValue = '';
  groupedDokumente: { [key: string]: FileUpload[] } = {};

  // FilePond
  @ViewChild('myPond') myPond: any;
  pondOptions = {
    class: 'my-filepond',
    credits: false,
    multiple: true,
    maxFiles: 10,
    allowFileTypeValidation: true,
    acceptedFileTypes: ['image/*', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain'],
    labelFileTypeNotAllowed: 'Dateityp nicht erlaubt',
    allowImageValidateSize: true,
    imageValidateSizeMinWidth: 150,
    imageValidateSizeMinHeight: 150,
    allowImageResize: true,
    imageResizeTargetWidth: 2400,
    imageResizeTargetHeight: 1600,
    imageResizeMode: 'contain',
    imageResizeUpscale: true,
    labelIdle: 'Dateien hier ablegen oder <a style="text-decoration:underline;">Durchsuchen</a> ...',
    server: {
      process: (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
        const formData = new FormData();
        formData.append(fieldName, file, file.name);
        formData.append('filepond', file.name);
        formData.append('table_name', this.tableName);
        formData.append('record_id', this.recordId);

        // Try to include user info so backend can store correct uploader
        try {
          const session = localStorage.getItem('session');
          if (session) {
            const sessionData = JSON.parse(session);
            const token: string | null = sessionData?.access_token || null;
            if (token) {
              const payload = JSON.parse(atob(token.split('.')[1]));
              const uid = payload?.uid ?? payload?.sub ?? payload?.userid ?? null;
              const uname = payload?.name ?? payload?.username ?? payload?.email ?? payload?.full_name ?? null;
              
              // Ensure we have valid user data
              if (uid && uid !== 'undefined' && uid !== 'null') {
                formData.append('uid', String(uid));
                console.log('FilePond upload: Setting user ID:', uid);
              } else {
                console.warn('FilePond upload: No valid user ID found in token payload');
              }
              
              if (uname && uname !== 'undefined' && uname !== 'null') {
                formData.append('uname', String(uname));
                console.log('FilePond upload: Setting user name:', uname);
              } else {
                console.warn('FilePond upload: No valid user name found in token payload');
              }
            } else {
              console.warn('FilePond upload: No access token found in session');
            }
          } else {
            console.warn('FilePond upload: No session data found');
          }
        } catch (e) {
          console.error('FilePond upload: Error parsing user info from token:', e);
        }

        const request = new XMLHttpRequest();
        request.open('POST', environment.apiUrl + '/filepond/process');

        // Add JWT token to request
        // Use the correct session key from auth storage
        const sessionKey = 'smarthcaap_auth_session_v1';
        const session = localStorage.getItem(sessionKey);
        let token: string | null = null;
        console.log('FilePond upload - Session key:', sessionKey);
        console.log('FilePond upload - Session exists:', !!session);
        console.log('FilePond upload - All localStorage keys:', Object.keys(localStorage));
        console.log('FilePond upload - Raw session data:', session);
        
        if (session) {
          try {
            const sessionData = JSON.parse(session);
            console.log('FilePond upload - Session data:', sessionData);
            token = sessionData.access_token;
            console.log('FilePond upload - Token exists:', !!token);
            console.log('FilePond upload - Token length:', token ? token.length : 0);
          } catch (e) {
            console.error('Error parsing session data:', e);
          }
        } else {
          console.warn('FilePond upload - No session found in localStorage');
          console.warn('FilePond upload - User needs to log in first!');
        }
        
        if (token) {
          request.setRequestHeader('Authorization', 'Bearer ' + token);
          console.log('FilePond upload - JWT token added:', token.substring(0, 20) + '...');
          console.log('FilePond upload - Authorization header set: Bearer ' + token.substring(0, 20) + '...');
        } else {
          console.warn('FilePond upload - No JWT token found in session data');
          console.warn('FilePond upload - Upload will fail with 401 Unauthorized');
          // Don't proceed with upload if no token
          error('Sie müssen sich zuerst anmelden, um Dateien hochzuladen.');
          return;
        }

        // Remove CORS headers - they should be set by the server, not the client
        // request.setRequestHeader('Access-Control-Allow-Origin', '*');
        // request.setRequestHeader('Access-Control-Allow-Headers', '*');

        request.upload.onprogress = (e) => {
          progress(e.lengthComputable, e.loaded, e.total);
        };

        request.onload = function () {
          console.log('FilePond upload - Response status:', request.status);
          console.log('FilePond upload - Response headers:', request.getAllResponseHeaders());
          console.log('FilePond upload - Response text:', request.responseText);
          
          if (request.status >= 200 && request.status < 300) {
            console.log('FilePond upload - Success, loading response');
            load(request.responseText);
          } else {
            console.error('FilePond upload - Failed with status:', request.status);
            console.error('FilePond upload - Error response:', request.responseText);
            error('Upload failed: ' + request.status + ' - ' + request.responseText);
          }
        };

        request.onerror = function () {
          console.error('FilePond upload network error');
          error('Network error during upload');
        };

        request.send(formData);

        return {
          abort: () => {
            request.abort();
            abort();
          },
        };
      },
      revert: 'filepond/revert',
      restore: 'filepond/restore?id=',
      fetch: 'filepond/fetch?data=',
      load: 'filepond/load',
    },
  };

  pondFiles = [];

  constructor(
    private dokumenteService: DokumenteService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.dataSource = new MatTableDataSource<FileUpload>([]);
  }

  ngOnInit() {
    console.log('DokumenteComponent: ngOnInit called with tableName:', this.tableName, 'recordId:', this.recordId);
    console.log('🔍 Component Context:', {
      isEmbedded: this.isEmbedded,
      parentContext: this.parentContext
    });
    
    // Debug JWT token
    this.checkJwtToken();
    
    if (this.isEmbedded) {
      this.showEmbeddedUI();
    } else {
      this.showStandaloneUI();
    }
    
    // Inject standort column dynamically for client aggregated view
    if (this.isClientView && !this.displayedColumns.includes('standort')) {
      this.displayedColumns = ['filename', 'standort', 'type_size', 'uploaded_by', 'upload_date', 'actions'];
    }
    this.loadDokumente();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['recordId'] && !changes['recordId'].firstChange) {
      console.log('DokumenteComponent: recordId changed from', changes['recordId'].previousValue, 'to', changes['recordId'].currentValue);
      this.loadDokumente();
    }
  }

  ngAfterViewInit() {
    if (this.paginator && this.sort) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
  }

  loadDokumente() {
    // Client aggregation mode: require clientId
    if (this.isClientView) {
      if (!this.clientId) {
        console.warn('No clientId provided for client documents aggregation');
        this.dataSource.data = [];
        this.totalItems = 0;
        return;
      }
      console.log('DokumenteComponent: Loading client aggregated documents for clientId:', this.clientId);
    } else {
      if (!this.recordId) {
        console.warn('No record ID provided for documents loading');
        return;
      }
      console.log('DokumenteComponent: Loading documents for tableName:', this.tableName, 'recordId:', this.recordId);
    }
    this.isLoadingResults = true;
    
    const page = this.paginator ? this.paginator.pageIndex + 1 : 1;
    const limit = this.paginator ? this.paginator.pageSize : 10;
    const sortBy = this.sort ? this.sort.active : 'filename';
    const sortOrder = this.sort ? this.sort.direction : 'asc';

    const obs = this.isClientView && this.clientId
      ? this.dokumenteService.getClientDokumente(this.clientId, page, limit)
      : this.dokumenteService.getDokumente(this.tableName, this.recordId, page, limit);

    obs.subscribe(
      (response: FileUploadResponse) => {
        console.log('Dokumente loaded successfully. Response:', response);
        
        if (this.isClientView && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // Handle grouped data for client view
          this.groupedDokumente = response.data as { [key: string]: FileUpload[] };
          // Flatten grouped data for table display
          const flatData: FileUpload[] = [];
          Object.keys(this.groupedDokumente).forEach(standortName => {
            flatData.push(...this.groupedDokumente[standortName]);
          });
          this.dataSource.data = flatData;
        } else {
          // Handle regular array data
          this.dataSource.data = response.data as FileUpload[] || [];
        }
        
        this.totalItems = response.total || 0;
        this.isLoadingResults = false;
        this.isRateLimitReached = false;
      },
      (error) => {
        console.error('Error loading dokumente for tableName:', this.tableName, 'recordId:', this.recordId, 'Error:', error);
        this.isLoadingResults = false;
        this.isRateLimitReached = true;
        this.snackBar.open('Fehler beim Laden der Dokumente', 'Schließen', { duration: 3000 });
      }
    );
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onPageChange() {
    this.loadDokumente();
  }

  onSortChange() {
    this.loadDokumente();
  }

  editDokument(dokument: FileUpload) {
    console.log('Edit dokument:', dokument);
    // For now, just show a message since we don't have a separate edit page
    this.snackBar.open('Bearbeitung wird in einer zukünftigen Version verfügbar sein', 'Schließen', { duration: 3000 });
  }

  deleteDokument(dokument: FileUpload) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Dokument löschen',
        message: `Sind Sie sicher, dass Sie das Dokument "${dokument.original_filename}" löschen möchten?`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dokumenteService.deleteDokument(dokument.id).subscribe(
          (response) => {
            this.snackBar.open('Dokument erfolgreich gelöscht', 'Schließen', { duration: 3000 });
            this.loadDokumente(); // Reload the data
          },
          (error) => {
            console.error('Error deleting dokument:', error);
            this.snackBar.open('Fehler beim Löschen des Dokuments', 'Schließen', { duration: 5000 });
          }
        );
      }
    });
  }

  addDokument() {
    console.log('Add new dokument for tableName:', this.tableName, 'recordId:', this.recordId);
    // FilePond upload is handled automatically
  }

  // FilePond event handlers
  pondHandleInit() {
    console.log('FilePond has initialised', this.myPond);
  }

  pondHandleAddFile(event: any) {
    console.log('A file was added', event);
  }

  onProcessFiles(event: any) {
    console.log('Files processed', event);
    // Reload the document list after upload
    this.loadDokumente();
  }

  pondHandleActivateFile(event: any) {
    console.log('A file was activated', event);
  }

  downloadDokument(dokument: FileUpload) {
    console.log('Download dokument:', dokument);
    this.dokumenteService.downloadDokument(dokument.id).subscribe(
      (response) => {
        // Handle file download
        const blob = new Blob([response], { type: dokument.mime_type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = dokument.original_filename;
        link.click();
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Dokument wird heruntergeladen', 'Schließen', { duration: 2000 });
      },
      (error) => {
        console.error('Error downloading dokument:', error);
        this.snackBar.open('Fehler beim Herunterladen des Dokuments', 'Schließen', { duration: 5000 });
      }
    );
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileTypeIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video_file';
    if (mimeType.startsWith('audio/')) return 'audio_file';
    if (mimeType.includes('pdf')) return 'picture_as_pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'description';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) return 'table_chart';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'slideshow';
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return 'folder_zip';
    if (mimeType.startsWith('text/')) return 'text_snippet';
    return 'insert_drive_file';
  }

  // Map MIME to display category
  getDisplayCategory(mimeType: string): string {
    if (!mimeType) return 'Dokument';
    if (mimeType.startsWith('image/')) return 'Bild';
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) return 'Kalkulation';
    if (mimeType.includes('word') || mimeType.includes('document') || mimeType.startsWith('text/')) return 'Dokument';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'Präsentation';
    if (mimeType.startsWith('video/')) return 'Video';
    if (mimeType.startsWith('audio/')) return 'Audio';
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return 'Archiv';
    return 'Dokument';
  }

  showEmbeddedUI(): void {
    console.log('🎯 Showing embedded UI for dokumente component');
    // Hide header elements, adjust styling for embedded view
    // This will be handled in the template with conditional rendering
  }

  showStandaloneUI(): void {
    console.log('🎯 Showing standalone UI for dokumente component');
    // Show full header, navigation, etc. for standalone view
    // This will be handled in the template with conditional rendering
  }

  // Debug method to check JWT token
  checkJwtToken(): void {
    const session = localStorage.getItem('session');
    console.log('JWT Token check:');
    console.log('- Session exists:', !!session);
    
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        const token = sessionData.access_token;
        console.log('- Token exists:', !!token);
        
        if (token) {
          console.log('- Token length:', token.length);
          console.log('- Token preview:', token.substring(0, 50) + '...');
          
          // Try to decode the token (without verification)
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            console.log('- Token payload:', payload);
            console.log('- Token expires:', new Date(payload.exp * 1000));
          } catch (e) {
            console.log('- Could not decode token payload');
          }
        }
        
        console.log('- Full session data:', sessionData);
      } catch (e) {
        console.error('Error parsing session data:', e);
      }
    }
  }

  // Helper method to get standort names for grouped view
  getStandortNames(): string[] {
    return Object.keys(this.groupedDokumente || {}).sort();
  }
}
